import React, { useState, useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";

//** ndbx */
import {
	Section,
	OneColumnStack,
	RHFCircleToggleGroupLabelled,
} from "@arp/react-ndbx";

//** MUI Components */
import {
	Typography,
	Skeleton,
	Stack,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CircleIcon from "@mui/icons-material/Circle";

//** Components */
import OutcomeCurrencyInputCard from "./OutcomeCurrencyInputCard.jsx";
import AdditionalInformation from "../additionalInformation/AdditionalInformation.jsx";
import NotificationCard from "../../coding/notificationCard/NotificationCard.jsx";

//** Context */
import usePreQualification from "../../../hooks/usePreQualification.ts";
import useSharedData from "../../../hooks/useSharedData.ts";
import { useGetReferenceData } from "../../../hooks/dataHooks/useGetReferenceData.js";
import { useOutletContext } from "react-router-dom";

//* Hooks
import { useUpdateCoverMutation } from "../../../hooks/dataHooks/useUpdateCoverMutation.js";
import { useIsFirstRender } from "../../../hooks/useIsFirstRender.ts";
import useNewBusiness from "../../../hooks/useNewBusiness.ts";
import { useOriginalAuraOutcome } from "../../../hooks/dataHooks/useOriginalAuraOutcome.js";

//** Config */
import { policyOutcomeDisclaimers } from "../../../config/disclaimers/disclaimers.jsx";
import { policyOutcomeFieldNames } from "../../../config/pageInfo/pageFieldName.js";
import { contactCenterPolicyOutcomeFieldNames } from "../../../config/pageInfo/pageFieldName.js";
import {
	questionTypes,
	questionLabels,
} from "../../../config/questions/questionItems.js";
import { selectCoverTooltips } from "../../../config/tooltips/pageTooltips.js";
import { Application } from "../../../config/const/enums.js";

//** Schema */
import {
	LIFE_INSURANCE_MIN,
	LIFE_INSURANCE_MAX,
	CRITICAL_ILLNESS_MIN,
	CRITICAL_ILLNESS_MAX,
	PERMANENT_DISABILITY_MIN,
	PERMANENT_DISABILITY_MAX,
} from "../../../schema/sharedConstValues.js";

//** Utilities */
import {
	formatCurrencyNoDecimal,
	formatCurrencyAbbreviation,
	formatCurrency,
	valueWithinRange,
} from "../../../utilities/numericFormat.js";
import { toUpperFirstCharacterFirstWord } from "../../../utilities/mappingHelper.js";
import { currencyStringToNumber } from "../../../utilities/numericFormat.js";

//** Mappings */
import { interviewAuraOutcomeMapping } from "../../../mapping/interviewAuraOutcomeMappingResponse.js";

//----------------------------------------------------------------

export default function SuccessfulOutcomeDetail({ ...props }) {
	const theme = useTheme();
	const firstUpdate = useRef(true);
	const isFirst = useIsFirstRender();

	const COVER_REQUIRING_UPDATE = {
		LIFE: "life",
		CI: "ci",
		CI_REMOVE: "ci_remove",
		PUW: "puw",
		PUW_REMOVE: "puw_remove",
		NONE: "none",
	};

	const { setValues, funcSet, dataSet } = usePreQualification();

	const [coverRequiringUpdate, setCoverRequiringUpdate] = useState(
		COVER_REQUIRING_UPDATE.NONE
	);

	const {
		person,
		frequency,
		application,
		onCheckedChange,
		onAdditionConditions,
		isRetrievePolicyExpired = false,
		retrieveQuoteJourney = false,
		isUWUser = false, //* Does the user have heightened underwriter privileges - NB. Very few users have this privilege.
	} = {
		...props,
	};

	//* Used for Application Flow.
	const isOnline = application === Application.ONLINE;
	const isContactCenter = application === Application.CONTACT_CENTER;

	//* Shared data
	const { handleEventChange, accessToken } = useSharedData();
	const [pageLoaded, setPageLoaded] = useState(false);

	//* Get reference data
	const { data: refData, isLoading: isRefDataLoading } = useGetReferenceData();
	const { auraExclusionWordingList } =
		isRefDataLoading === false ? refData : {};

	//** Get the Original Outcome data.
	//** We use the Original Outcome data so that we can track the initial Interview Outcome */
	const {
		data: originalOutcomeData,
		isLoading: isOriginalAuraOutcomeDataLoading,
	} = useOriginalAuraOutcome({ enabled: true });

	//* Store the original Aura outcome
	const originalMappedData = useRef(
		retrieveQuoteJourney
			? originalOutcomeData
			: interviewAuraOutcomeMapping(originalOutcomeData?.data)
	);

	const {
		data: outcomeData,
		updateCoverData,
		updateLifeCover,
		isDataUpdating: outcomeIsLoading,
	} = useNewBusiness();

	//* Update cover mutation
	const updateCoverMutationContext = useUpdateCoverMutation();

	const {
		mutate: updateCoverMutate,
		data: updateCoverMutateData,
		isSuccess: isUpdateCoverSuccess,
		isLoading: isUpdateCoverLoading,
		error: mutError,
	} = { ...updateCoverMutationContext };

	const [open, setOpen] = useState(false);
	useEffect(() => {
		//! HERE - Update the newBusiness State
		if (isUpdateCoverLoading === false && updateCoverMutateData) {
			updateCoverData(updateCoverMutateData);
		}
		setOpen(isUpdateCoverLoading);
	}, [isUpdateCoverLoading, updateCoverMutateData]);

	const {
		methods: [methods],
		validateOutcomeTrauma: [validateOutcomeTrauma],
		errors: [errors],
		handleApplyCheckbox: [handleApplyCheckbox],
	} = useOutletContext();

	const { control, setValue, clearErrors, resetField, trigger } = {
		...methods,
	};

	let criticalIllnessWatch = "";
	let permanentDisabilityWatch = "";

	//----------------------------------------------------------------
	function anyErrors() {
		let foundErrors = Object?.keys(errors).length > 0 ? true : false;

		//! This is a hack. If they only error on that page is
		//! accept terms checkbox, we still want to be able to
		//! update the covers (if we need to)
		if (
			Object?.keys(errors)?.length === 1 &&
			Object.values(errors)[0]?.ref?.name === "acceptOffer"
		) {
			foundErrors = false;
		}
		return foundErrors;
	}
	//!----------------------------------------------------------------
	//!----------------------------------------------------------------
	/* #region Life Cover */
	//** TODO:  1. Life Cover Functionality - Start */
	const [lifeCoverValid, setLifeCoverValid] = useState(true); //* A flag to determine if the value is valid
	const [isLifeCoverUpdated, setIsLifeCoverUpdated] = useState(true); //* Have we changed the value? We need to to trigger a call to updateCover
	// const [coverUpdated, setCoverUpdated] = useState(false); //* This is set when we leave the field. TODO: I DON'T THINK THIS IS NEEDED.

	//* Watch for changes in the Life Cover amount
	const lifeCoverWatch = useWatch({
		control,
		name: policyOutcomeFieldNames(person).LIFE_COVER,
	});

	//* check to see if the Life Cover has been changed
	useEffect(() => {
		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}

		if (
			firstUpdate.current ||
			outcomeData?.covers?.life?.sumInsured.toString() ===
				lifeCoverWatch?.toString()
		) {
			setLifeCoverValid(true);
			return;
		}

		setLifeCoverValid(
			valueWithinRange(
				lifeCoverWatch,
				LIFE_INSURANCE_MIN[outcomeData?.ageInformation?.minAgeGroup],
				LIFE_INSURANCE_MAX[outcomeData?.ageInformation?.maxAgeGroup]
			)
		);
		setIsLifeCoverUpdated(false);
		//* Set the Shared Context API
		handleEventChange({
			name: "isCoverStateValid",
			value: false,
		});
	}, [lifeCoverWatch]);

	//** Life Cover Functionality - End */
	/* #endregion */

	//!----------------------------------------------------------------
	//!----------------------------------------------------------------
	/* #region Critical Illness Cover */
	//** TODO: 2. Critical Illness Functionality - Start */
	//* Has the Critical Illness value changed
	const [showAllTraumaCover, setShowAllTraumaCover] = useState(false); //* Show the yes/no component.
	const [isCriticalIllnessCoverUpdated, setIsCriticalIllnessCoverUpdated] =
		useState(true); //* Is the component updated and valid.

	//* To Confirm, but this state variable appears to determine if
	//* the trauma questions are displayed on page load.
	const [showTrauma, setShowTrauma] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover || false
	);

	const [callUpdateOnCriticalIllness, setCallUpdateOnCriticalIllness] =
		useState(false); //* This is the SoT for when we call an update for Critical Illness cover.
	//* What should cause this to be 'true'
	//* 1. No is selected on the trauma question.
	//* 2. The checkbox is unchecked when there was a value set in CI currency component
	//* 3. The CI currency component was manually cleared and the user tabs off.

	//* Watch for changes in the Critical Illness Cover amount
	criticalIllnessWatch = useWatch({
		control,
		name: policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER,
	});

	const checkCIRangeAndValue = () => {
		const validRangeAndValue =
			valueWithinRange(
				criticalIllnessWatch,
				CRITICAL_ILLNESS_MIN[outcomeData?.ageInformation?.minAgeGroup],
				CRITICAL_ILLNESS_MAX[outcomeData?.ageInformation?.maxAgeGroup]
			) && Number(criticalIllnessWatch) <= Number(lifeCoverWatch) === true;

		return validRangeAndValue;
	};

	useEffect(() => {
		const runCriticalIllnessCheck = async () => {
			if (isRetrievePolicyExpired === true) {
				setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
				return;
			}

			if (checkValueOfLifeCover(lifeCoverWatch) === true) {
				setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
				return;
			}

			if (criticalIllnessWatch?.toString == "") {
				validateOutcomeTrauma(false);
			}

			if (isUpdateCoverLoading === true) return;
			if (
				firstUpdate.current ||
				outcomeData?.covers?.criticalIllness?.sumInsured?.toString() ===
					criticalIllnessWatch?.toString()
			) {
				setIsCIValidAmtValid(true);
				return;
			}
			// if (checkCIRangeAndValue()) {
			if (isCIChecked === true && checkCIRangeAndValue() === true) {
				setShowTrauma(true);
				setShowAllTraumaCover(true);
				validateOutcomeTrauma(true); //* Tell the yup validation that we need to validate the Trauma Question.
				setIsCIValidAmtValid(true);
				setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
				await new Promise((resolve) => setTimeout(resolve, 0));
				await trigger(policyOutcomeFieldNames(person).TRAUMA_COVER);
			} else {
				// when you don't show the trauma cover set its value and error to empty
				validateOutcomeTrauma(false);
				setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
				clearErrors(policyOutcomeFieldNames(person).TRAUMA_COVER);
				setShowTrauma(false);
				setShowAllTraumaCover(false);
				setIsCIValidAmtValid(false);
			}
			setIsCriticalIllnessCoverUpdated(false);
			// setTraumaSelectValid(false);
			//* Set the Shared Context API
			handleEventChange({
				name: "isCoverStateValid",
				value: false,
			});
		};

		runCriticalIllnessCheck();
	}, [criticalIllnessWatch, trigger]);

	//* We only want to hide the Trauma question when it's been updated
	useEffect(() => {
		setShowAllTraumaCover(showTrauma);
	}, [showTrauma]);
	//** */

	//* Watch and Listen for the Trauma Cover selected
	//? Remember to add this to the state
	// const [traumaSelectValid, setTraumaSelectValid] = useState(false);
	const traumaSelect = useWatch({
		control,
		name: policyOutcomeFieldNames(person).TRAUMA_COVER,
	});

	const [doINeedToUpdateCI, setDoINeedToUpdateCI] = useState(false);
	useEffect(() => {
		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}

		if (isUpdateCoverLoading === true) return;
		const updateStateValue = async () => {
			// if(criticalIllnessWatch === dataSet?.covers?.lifeCover)
			let updateObject = {
				amount: criticalIllnessWatch,
				type: "update_question",
				person: person,
			};

			funcSet.updateCriticalIllness(updateObject);
			//* If the current traumaCover is the same as the set traumaSelect
			//* we don't want to call an update
			// if (dataSet[person]?.traumaCover === traumaSelect) return;
			setDoINeedToUpdateCI(true);
		};

		updateStateValue();
	}, [traumaSelect]);

	useEffect(() => {
		if (isFirst) return;

		if (doINeedToUpdateCI === true && showTrauma === true) {
			const outputTraumaQuestion = traumaSelect === "no";
			// setTraumaSelectValid(outputTraumaQuestion);
			if (outputTraumaQuestion === true) {
				setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.CI);
				setDoINeedToUpdateCI(false);
			}
		}
	}, [dataSet, dataSet?.dataUpdating, doINeedToUpdateCI]);

	//! *** HANDLE CRITICAL ILLNESS CHECKBOX CHECK -  START */
	const [upSellSelected, setUpSellSelected] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover === false &&
			!outcomeData?.covers?.hasCriticalIllnessUpSell
	);

	const handleCriticalIllnessCheckedChange = async (e) => {
		e.preventDefault();

		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}
		//? We need to
		//? 1. Clear any errors under the trauma question
		//? 2. Rest the trauma question
		//? 3. Hide the trauma question
		//? 4. Clear the monthly/yearly premium amounts
		//? 5. Clear the CI amount field
		//? 6. Update local state
		//? 6a  _illnessCoverPremium"
		//? 6b _traumaCover
		//? 7. Set a flag to say that we are REMOVING CI
		//? 8. Call updateCover
		resetCriticalIllnessState(e);
	};

	const [resetCIAdditional, setResetCIAdditional] = useState(false);
	const resetCriticalIllnessState = (e) => {
		e.preventDefault();
		if (!e) return;
		const { name } = e?.target;
		const split = name.split("_");

		resetField(name);
		setShowAllTraumaCover(false);
		// validateOutcomeTrauma(false);
		setShowCIExclusions(false);
		setShowCILoading(false);
		setShowCIHeadline(false);
		setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
		clearErrors(policyOutcomeFieldNames(person).TRAUMA_COVER);

		const isChecked = e?.target.checked;
		setIsCIChecked(isChecked); //* Is the checkbox being checked or unchecked?

		setCriticalIllnessChecked(isChecked); //* Is the checkbox being checked or unchecked?

		if (isChecked === false) {
			setIsCIValidAmtValid(false);
			setShowCIExclusions(false);
			setShowCILoading(false);
			setShowCIHeadline(false);
			validateOutcomeTrauma(false);

			//* Reset the CI Additional Information
			if (isUWUser === true) {
				setResetCIAdditional(true);
			}
		}

		const updateField = split[0] + "_illnessCoverPremium";
		const updateQuestion = split[0] + "_traumaCover";
		const shouldUseCIValue = "shouldUseIllnessCover";

		//* Determine if the box was checked while
		//* there is upsell being offered.
		const checkedAndUpSell =
			isChecked === true &&
			outcomeData?.covers?.hasCriticalIllnessUpSell === true;

		if (checkedAndUpSell === true) {
			checkedWithUpSell(true);

			setValue(
				policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER,
				outcomeData?.covers?.criticalIllness?.sumInsured,
				{ shouldDirty: true }
			);
			let updateObject = {
				[shouldUseCIValue]: true,
				type: "update_use_flag",
				person: split[0],
			};

			funcSet.updateCriticalIllness(updateObject);
			//* We need to make sure that the upSell value is still valid
			checkValueOfLifeCover(lifeCoverWatch, true);
		}

		if (outcomeData?.covers?.hasCriticalIllnessUpSell === true) {
			checkedWithUpSell(isChecked);
			let updateObject = {
				[shouldUseCIValue]: isChecked,
				type: "update_use_flag",
				person: split[0],
			};
			funcSet.updateCriticalIllness(updateObject);
		}

		if (outcomeData?.covers?.hasCriticalIllnessUpSell === false) {
			//* Make sure we remove the amount from the dataSet
			//* when we check or uncheck the checkbox
			let updateObject = {
				[updateField]: "",
				[updateQuestion]: "",
				type: "remove",
				person: split[0],
			};

			funcSet.updateCriticalIllness(updateObject);
		}

		//* If we have Critical Illness upSell and we checked the checkbox
		setUpSellSelected(checkedAndUpSell);

		setCoverRequiringUpdate(
			e?.target.checked === true
				? COVER_REQUIRING_UPDATE.NONE
				: COVER_REQUIRING_UPDATE.CI
		);
	};

	const checkedWithUpSell = (boolValue) => {
		//* based on the boolValue true/false.
		//* show/hide the premiums (set to the selected value)
		setShowCIPremiums(boolValue);
		//* Hide special terms. We never have special terms with upsell
		setShowCIExclusions(false);
		//* show/hide trauma questions
		setShowTrauma(boolValue);
		setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
		clearErrors(policyOutcomeFieldNames(person).TRAUMA_COVER);
	};
	//! *** HANDLE CRITICAL ILLNESS CHECKBOX CHECK -  END */

	/* #endregion Critical Illness Functionality - End */
	//!----------------------------------------------------------------
	//!----------------------------------------------------------------
	/* #region NEW Critical Illness Cover */
	const [isCriticalIllnessUpdated, setIsCriticalIllnessUpdated] =
		useState(true);

	//? We use this to know if the CI amount field is populated and valid
	const [isCIValidAmtValid, setIsCIValidAmtValid] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover || false
	);
	//? CI Checkbox
	const [isCIChecked, setIsCIChecked] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover || false
	);
	//? CI Headline
	const [showCIHeadline, setShowCIHeadline] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover ||
			outcomeData?.covers?.hasCriticalIllnessUpSell ||
			false
	);
	//? CI Premiums
	const [showCIPremiums, setShowCIPremiums] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover || false
	);
	//? Special Terms (in Special Terms and Conditions)
	const [showCIExclusions, setShowCIExclusions] = useState(
		outcomeData?.covers?.hasCriticalIllnessExclusion || false
	);
	//? Loading (in Special Terms and Conditions)
	const [showCILoading, setShowCILoading] = useState(
		outcomeData?.covers?.hasCriticalIllnessLoading || false
	);
	//? Does the CI Cover have either exclusions or loadings?
	const [isCIExclusionsOrLoading, setIsCIExclusionsOrLoading] = useState(
		outcomeData?.covers?.hasCriticalIllnessExclusionOrLoadings || false
	);

	//* If we had unable to work and cleared it, we still want to be able to allow the user to add
	//* it back in.
	const [showCriticalIllness, setShowCriticalIllness] = useState(
		originalMappedData?.current?.covers?.hasCriticalIllnessCover || false
	);

	useEffect(() => {
		if (!outcomeData) return;

		setIsCIValidAmtValid(outcomeData?.covers?.hasCriticalIllnessCover);
		setIsCIChecked(outcomeData?.covers?.hasCriticalIllnessCover);
		setShowCIHeadline(
			outcomeData?.covers?.hasCriticalIllnessCover &&
				outcomeData?.covers?.hasCriticalIllnessUpSell === false
		);
		setShowCIPremiums(outcomeData?.covers?.hasCriticalIllnessCover);
		setShowCIExclusions(
			outcomeData?.covers?.hasCriticalIllnessCover &&
				outcomeData?.covers?.hasCriticalIllnessExclusion
		);
		setShowCILoading(
			outcomeData?.covers?.hasCriticalIllnessCover &&
				outcomeData?.covers?.hasCriticalIllnessLoading
		);
		setIsCIExclusionsOrLoading(
			outcomeData?.covers?.hasCriticalIllnessExclusionOrLoadings
		);

		setShowTrauma(outcomeData?.covers?.hasCriticalIllnessCover);
	}, [outcomeData?.covers]);

	useEffect(() => {
		setShowCIPremiums(
			outcomeData?.covers?.hasCriticalIllnessCover &&
				isCriticalIllnessUpdated &&
				isCIValidAmtValid
		);
		setShowCIExclusions(
			outcomeData?.covers?.hasCriticalIllnessExclusion &&
				isCriticalIllnessUpdated &&
				isCIValidAmtValid
		);
		setShowCILoading(
			outcomeData?.covers?.hasCriticalIllnessLoading &&
				isCriticalIllnessUpdated &&
				isCIValidAmtValid
		);

		setIsCIExclusionsOrLoading(
			outcomeData?.covers?.hasCriticalIllnessExclusionOrLoadings &&
				isCriticalIllnessUpdated &&
				isCIValidAmtValid
		);

		setShowCriticalIllness(
			originalMappedData?.current?.covers?.hasCriticalIllnessCover
		);
	}, [isCriticalIllnessUpdated, isCIValidAmtValid]);

	// To get the current value
	// const showCriticalIllness = showCriticalIllnessRef.current;

	// useEffect(() => {
	//   showCriticalIllnessRef.current =
	//     outcomeData?.covers?.memoHasCriticalIllnessCover;
	// }, [outcomeData]);

	/* endregion NEW Critical Illness Cover */

	/* #region PUW Cover */
	//** 3. TODO: Permanently unable to work - Start */
	//* Has the PUW value changed? This is set to false
	//* when the PUW value has changed but not yet had the
	//* quote updated.
	const [isPermanentDisabilityUpdated, setIsPermanentDisabilityUpdated] =
		useState(true);

	//? We use this to know if the PUW amount field is populated and valid
	const [isPUWValidAmtValid, setIsPUWValidAmtValid] = useState(
		outcomeData?.covers?.hasUnableToWorkCover || false
	);
	//? PUW Checkbox
	const [isPUWChecked, setIsPUWChecked] = useState(
		outcomeData?.covers?.hasUnableToWorkCover || false
	);
	//? PUW Headline
	const [showPUWHeadline, setShowPUWHeadline] = useState(
		outcomeData?.covers?.hasUnableToWorkCover || false
	);
	//? Premiums
	const [showPUWPremiums, setShowPUWPremiums] = useState(
		outcomeData?.covers?.hasUnableToWorkCover || false
	);
	//? Special Terms (in Special Terms and Conditions)
	const [showPUWExclusions, setShowPUWExclusions] = useState(
		outcomeData?.covers?.hasUnableToWorkExclusion || false
	);
	//? Loading (in Special Terms and Conditions)
	const [showPUWLoading, setShowPUWLoading] = useState(
		outcomeData?.covers?.hasUnableToWorkLoading || false
	);
	//? Does the PUW Cover have either exclusions or loadings?
	const [isPUWExclusionsOrLoading, setIsPUWExclusionsOrLoading] = useState(
		outcomeData?.covers?.hasUnableToWorkExclusionOrLoadings || false
	);

	//* If we had unable to work and cleared it, we still want to be able to allow the user to add
	//* it back in.
	const [showUnableToWork, setShowUnableToWork] = useState(
		originalMappedData?.current?.covers?.hasUnableToWorkCover || false
	);

	useEffect(() => {
		if (!outcomeData) return;

		setIsPUWValidAmtValid(outcomeData?.covers?.hasUnableToWorkCover);
		setIsPUWChecked(outcomeData?.covers?.hasUnableToWorkCover);
		setShowPUWHeadline(outcomeData?.covers?.hasUnableToWorkCover);
		setShowPUWPremiums(outcomeData?.covers?.hasUnableToWorkCover);
		setShowPUWExclusions(
			outcomeData?.covers?.hasUnableToWorkCover &&
				outcomeData?.covers?.hasUnableToWorkExclusion
		);
		setShowPUWLoading(
			outcomeData?.covers?.hasUnableToWorkCover &&
				outcomeData?.covers?.hasUnableToWorkLoading
		);
		setIsPUWExclusionsOrLoading(
			outcomeData?.covers?.hasUnableToWorkExclusionOrLoadings
		);

		setShowUnableToWork(
			originalMappedData?.current?.covers?.hasUnableToWorkCover
		);
	}, [outcomeData?.covers]);

	useEffect(() => {
		setShowPUWPremiums(
			outcomeData?.covers?.hasUnableToWorkCover &&
				isPermanentDisabilityUpdated &&
				isPUWValidAmtValid
		);
		setShowPUWExclusions(
			outcomeData?.covers?.hasUnableToWorkExclusion &&
				isPermanentDisabilityUpdated &&
				isPUWValidAmtValid
		);
		setShowPUWLoading(
			outcomeData?.covers?.hasUnableToWorkLoading &&
				isPermanentDisabilityUpdated &&
				isPUWValidAmtValid
		);

		setIsPUWExclusionsOrLoading(
			outcomeData?.covers?.hasUnableToWorkExclusionOrLoadings &&
				isPermanentDisabilityUpdated &&
				isPUWValidAmtValid
		);
	}, [isPermanentDisabilityUpdated, isPUWValidAmtValid]);

	//! TEST
	useEffect(() => {
		const anyExclusionsOrLoadings =
			outcomeData?.covers?.anyExclusionsOrLoadings;
		const lifeExtra = outcomeData?.covers?.hasLifeExclusionOrLoadings;

		//* If we have any any Life exclusions or loading, show the extra T&C
		if (lifeExtra || !anyExclusionsOrLoadings) {
			onCheckedChange(lifeExtra);
			handleApplyCheckbox(false);
			return;
		}

		//* If we have no Life cover exclusions but we DO have PUW exclusions or loading
		//* AND the PUW amount is valid, show the additional T&C
		const puwExtra = isPUWExclusionsOrLoading && isPUWValidAmtValid;

		//! TODO: Do the same for CI

		//* If we have no Life cover exclusions but we DO have CI exclusions or loading
		//* AND the CI amount is valid, show the additional T&C
		const ciExtra = isCIExclusionsOrLoading && isCIValidAmtValid;

		//! TODO: Do the same for CI

		const showPUWExtra = puwExtra && isPUWChecked;
		const showCIExtra = ciExtra && isCIChecked;
		const showExtra = showPUWExtra || showCIExtra;

		onCheckedChange(showExtra);
		handleApplyCheckbox(!showExtra);

		if (isCIChecked === false) {
			validateOutcomeTrauma(false);
		}
	}, [
		showPUWExclusions,
		showPUWLoading,
		isPUWChecked,
		showCIExclusions,
		showCILoading,
		isCIChecked,
	]);

	// useEffect(() => {
	//   const lifeExtra = outcomeData?.covers?.hasLifeExclusionOrLoadings;

	//   if (lifeExtra === true) {
	//     handleApplyCheckbox(!isPUWChecked);
	//   }

	//   handleApplyCheckbox(!isPUWChecked);
	// }, [isPUWChecked]);
	//! TEST

	//!----------------------------------------------------------------

	permanentDisabilityWatch = useWatch({
		control,
		name: policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER,
	});

	//* ----------------------------------------------------------------
	// TODO: SUNDAY 15th Oct
	//* Display state */
	//! TODO:  NOTE: We only show the exclusions notes and checkbox if there are exclusions
	//! IF for example, there are no Life exclusions but there are in the
	//! other two covers and we unselect them, we should hide the exclusion notes and checkbox
	// const [hasCoverExclusions, setHasCoverExclusions] = useState(false); //* Default from state
	//& Show the header if there is onLoad, if hasUnableToWorkCover is true
	//& from there, we will manage the showPUWHeadline within this component.

	//* Display state */
	//* ----------------------------------------------------------------
	useEffect(() => {
		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}
		if (isUpdateCoverLoading === true) return;

		if (
			firstUpdate.current ||
			outcomeData?.covers?.unableToWork?.sumInsured?.toString() ===
				permanentDisabilityWatch?.toString()
		) {
			setIsPUWValidAmtValid(true);
			return;
		}

		if (
			valueWithinRange(
				permanentDisabilityWatch,
				PERMANENT_DISABILITY_MIN[outcomeData?.ageInformation?.minAgeGroup],
				PERMANENT_DISABILITY_MAX[outcomeData?.ageInformation?.maxAgeGroup]
			)
		) {
			setIsPUWValidAmtValid(true);
		} else {
			setIsPUWValidAmtValid(false);
		}
		setIsPermanentDisabilityUpdated(false);
		handleEventChange({
			name: "isCoverStateValid",
			value: false,
		});
	}, [permanentDisabilityWatch]);

	//! *** HANDLE Permanently Unable to Work CHECKBOX CHECK -  START */
	const handleUnableToWorkCheckedChange = async (e) => {
		e.preventDefault();

		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}
		// We need to
		// 4. Clear the monthly/yearly premium amounts
		// 5. Clear the PUW amount field
		// 6. Update local state
		// 6a  _permanentDisabilityCoverPremium"
		// 6b _traumaCover
		// 7. Set a flag to say that we are REMOVING CI
		// 8. Call updateCover
		resetUnableToWorkState(e);
		// setParentStateOnCoverChecked();
	};

	const resetUnableToWorkState = (e) => {
		e.preventDefault();
		if (!e) return;
		const { name } = e?.target;
		const split = name.split("_");

		resetField(name);
		// setPermanentDisabilityCoverAmount("");
		const isChecked = e?.target.checked;
		setIsPUWChecked(isChecked); //* Is the checkbox being checked or unchecked?
		if (isChecked === false) {
			setIsPUWValidAmtValid(false);
			setShowPUWExclusions(false);
			setShowPUWLoading(false);
			setShowPUWHeadline(false);
		}

		const updateField = split[0] + "_permanentDisabilityCoverPremium";

		let updateObject = {
			[updateField]: "",
			type: "remove_keep_occupation",
			person: split[0],
		};

		funcSet.updatePermanentDisabilityCover(updateObject);
		setCoverRequiringUpdate(
			e?.target.checked === true
				? COVER_REQUIRING_UPDATE.NONE
				: COVER_REQUIRING_UPDATE.PUW
		);
	};
	//! *** HANDLE Permanently Unable to Work CHECKBOX CHECK -  START */
	//** Permanently unable to work - End */

	/* #endregion */
	//!----------------------------------------------------------------

	//** Checkbox check  */
	const [criticalIllnessChecked, setCriticalIllnessChecked] = useState(
		outcomeData?.covers?.hasCriticalIllnessCover || false
	);

	useEffect(() => {
		setCriticalIllnessChecked(outcomeData?.covers?.hasCriticalIllnessCover);
	}, [outcomeData?.covers?.hasCriticalIllnessCover]);

	useEffect(() => {}, [criticalIllnessChecked]);
	//** Checkbox check  */

	//----------------------------------------------------------------
	//** Handle the currency onBlur event - Start **/

	const checkValueOfLifeCover = async (coverValue, upSellCheck = false) => {
		//* Neither CI or PUW cover can be greater than Life cover
		let isLifeCoverValid = true;
		let criticalIllnessLessThanLifeCover = true;
		let unableToWorkLessThanLifeCover = true;

		if (coverValue === "") return isLifeCoverValid;

		if (
			criticalIllnessWatch != null &&
			criticalIllnessWatch !== "" &&
			(isCIChecked === true || upSellCheck === true)
		) {
			criticalIllnessLessThanLifeCover =
				Number(coverValue) < Number(criticalIllnessWatch) ? false : true;

			await setErrorOnCriticalIllness(criticalIllnessLessThanLifeCover);
		}

		if (
			permanentDisabilityWatch != null &&
			permanentDisabilityWatch !== "" &&
			isPUWChecked === true
		) {
			unableToWorkLessThanLifeCover =
				Number(coverValue) < Number(permanentDisabilityWatch) ? false : true;

			await setErrorOnUnableToWork(unableToWorkLessThanLifeCover);
		}
		isLifeCoverValid =
			criticalIllnessLessThanLifeCover && unableToWorkLessThanLifeCover;
		return isLifeCoverValid;
	};

	const setErrorOnCriticalIllness = async (criticalIllnessLessThan) => {
		if (criticalIllnessLessThan === false || isCIChecked === false) {
			setIsCriticalIllnessCoverUpdated(false);
			trigger(policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER);
			// setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
			clearErrors(policyOutcomeFieldNames(person).TRAUMA_COVER);
			setShowAllTraumaCover(false);
			// validateOutcomeTrauma(false);
		} else {
			clearErrors(policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER);
			setShowAllTraumaCover(true);
			validateOutcomeTrauma(true);
			trigger(policyOutcomeFieldNames(person).TRAUMA_COVER);
		}
	};

	const setErrorOnUnableToWork = async (unableToWorkLessThan) => {
		if (unableToWorkLessThan === false) {
			setIsPermanentDisabilityUpdated(false);
			trigger(policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER);
		} else {
			clearErrors(policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER);
		}
	};
	//! ----------------------------------------------------------------
	// #region ON BLUR - START
	const handleOnBlur = async (e) => {
		e.preventDefault();
		if (isRetrievePolicyExpired === true) {
			setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
			return;
		}

		// setCoverUpdated(false);
		// if (anyErrors() === true) return;
		//* First thing we need to do is check to ensure that the
		//* Life Cover is not less than the Optional Covers.

		//** When we onnBlur (Leave a field) for any of the covers,  */
		//** We need to call update cover  */
		//** Note: Ensure that that field is valid before trying to make the call. */
		if (!e) return;
		const { name, value } = e?.target;
		const split = name.split("_");
		const valueAmt = value;

		switch (split[1]) {
			case "lifeCover": {
				const valueAsInteger =
					value === "" ? "" : parseFloat(currencyStringToNumber(value), 0);

				let updateObject = { [e?.target.name]: valueAsInteger };
				await setValues(updateObject);
				await updateLifeCover(valueAsInteger);

				const isFormValid = await checkValueOfLifeCover(valueAsInteger);

				//* We only want the form to update if it is valid
				if (isFormValid === false) break;

				// setCoverUpdated(true);
				setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.LIFE);
				//* Validate the trauma question, if required.
				// if (isCIChecked === true || checkCIRangeAndValue() === true) {
				if (isCIChecked === true && checkCIRangeAndValue() === true) {
					validateOutcomeTrauma(true);
					trigger(policyOutcomeFieldNames(person).TRAUMA_COVER);
				} else {
					validateOutcomeTrauma(false);
				}
				break;
			}
			case "illnessCover": {
				// trigger(policyOutcomeFieldNames(person).TRAUMA_COVER);
				if (valueAmt === "" || valueAmt == null) {
					// Clean up the events
					resetCriticalIllnessState(e);
					break;
				} else {
					const valueAsInteger =
						value === "" ? "" : parseFloat(currencyStringToNumber(value), 10);

					let updateObject = {
						amount: valueAsInteger,
						type: "add",
						person: person,
					};

					funcSet.updateCriticalIllness(updateObject);
					setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
				}
				//! TODO: Determine why we need this here?
				trigger(policyOutcomeFieldNames(person).TRAUMA_COVER);
				break;
			}
			case "permanentDisabilityCover": {
				if (valueAmt === "" || valueAmt == null) {
					// Clean up the events
					resetUnableToWorkState(e);
					break;
				} else {
					const valueAsInteger =
						value === "" ? "" : parseFloat(currencyStringToNumber(value), 10);

					let updateObject = {
						amount: valueAsInteger,
						type: "add",
						person: person,
					};

					funcSet.updatePermanentDisabilityCover(updateObject);
					setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.PUW);
					break;
				}
			}
			default: {
				break;
			}
		}
	};
	// #endregion ON BLUR - END

	//! ----------------------------------------------------------------
	// const [currentPerson, setCurrentPerson] = useState(null);

	//* On Page Load **/
	useEffect(() => {
		cleanUpCoverEvents(outcomeData);

		//* make sure that all the update flags are
		//* reset on load
		firstUpdate.current = false;
		setIsLifeCoverUpdated(true);
		setIsCriticalIllnessCoverUpdated(true);
		// setTraumaSelectValid(false);
		setIsPermanentDisabilityUpdated(true);

		//* Let the layout page know we are doing updates.
		handleEventChange({
			name: "updatingQuoteSummary",
			value: true,
		});

		setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);
	}, []);

	useEffect(() => {
		//* defaultValues are cached on the first render within the custom hook.
		//* We need to reset the defaultValues as on first render, the dataSet is null.
		//* So, use reset to reset the form manually to the updated values in the dataSet
		//* in order to reset the Controller components' value.
		if (
			!outcomeData ||
			outcomeData?.isDataUpdating === true ||
			outcomeData?.covers?.life?.sumInsured === ""
		)
			return;
		if (pageLoaded === true) return; //* If this already ran, don't run it again.
		//* We only want to set the cover values if they are needed.
		//* This helps ensure that the validation works as expected.
		// HERE
		let ciCover =
			!isNaN(outcomeData?.covers?.criticalIllness?.sumInsured) &&
			outcomeData?.covers?.criticalIllness?.sumInsured?.toString().length > 0;
		let puwCover =
			!isNaN(outcomeData?.covers?.unableToWork?.sumInsured) &&
			outcomeData?.covers?.unableToWork?.sumInsured?.toString().length > 0;

		let moreThanLifeCover = ciCover || puwCover;

		setValue(
			policyOutcomeFieldNames(person).LIFE_COVER,
			outcomeData?.covers?.life?.sumInsured,
			{ shouldDirty: moreThanLifeCover }
		);

		if (ciCover) {
			// HERE
			setValue(
				policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER,
				outcomeData?.covers?.criticalIllness?.sumInsured,
				{ shouldDirty: true }
			);
			setValue(
				policyOutcomeFieldNames(person).TRAUMA_COVER,
				outcomeData?.covers?.hasCriticalIllnessCover ? "no" : "",
				{ shouldDirty: true }
			);
		}

		if (puwCover) {
			setValue(
				policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER,
				outcomeData?.covers?.unableToWork?.sumInsured,
				{ shouldDirty: true }
			);
		}

		//* We only want this to run once, set page loaded to true
		setPageLoaded(true);
	}, [outcomeData]);

	useEffect(() => {
		if (outcomeIsLoading === true) return;
		setValue(
			policyOutcomeFieldNames(person).LIFE_COVER,
			outcomeData?.covers?.life?.sumInsured,
			{ shouldDirty: !outcomeData?.covers?.anyExclusions }
		);
	}, [outcomeData]);

	const cleanUpCoverEvents = (outcomeData) => {
		if (outcomeData?.covers?.criticalIllness?.sumInsured === "") {
			cleanUpIllnessCover();
		}
		if (outcomeData?.covers?.unableToWork?.sumInsured === "") {
			cleanUpPermanentDisabilityCover();
		}
	};

	//* Reset everything associated with Critical Illness Cover
	const cleanUpPermanentDisabilityCover = () => {
		try {
			resetField(policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER);
		} catch (ex) {
			console.log("Error occurred - ", ex);
		}
	};

	//* Reset everything associated with Permanent Disability Cover
	const cleanUpIllnessCover = () => {
		// criticalIllnessWatch = "";
		// resetField(policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER);
		// setShowAllTraumaCover(false);
		// validateOutcomeTrauma(false, person);
		// setValue(policyOutcomeFieldNames(person).TRAUMA_COVER, "");
		// clearErrors(policyOutcomeFieldNames(person).TRAUMA_COVER);
	};

	//* Check that the update has been successful.
	//* We should only reset the the update states for each cover
	//* if they were valid and allowed to be updated.
	//* for example, if we update critical illness but don't
	//* click 'No' on the question, then don't update.
	useEffect(() => {
		setIsLifeCoverUpdated(
			outcomeData?.covers?.hasLifeCover === true ? true : isUpdateCoverSuccess
		);
		setIsCriticalIllnessCoverUpdated(
			outcomeData?.covers?.hasCriticalIllnessCover === true
				? true
				: isUpdateCoverSuccess
		);
		setIsPermanentDisabilityUpdated(
			outcomeData?.covers?.hasUnableToWorkCover === true
				? true
				: isUpdateCoverSuccess
		);

		//* This is the SoT for when Critical Illness should be updated.
		setCallUpdateOnCriticalIllness(false);

		//* After a successful update, rest the flag to tell
		//* our component that nothing needs to be updated.
		setCoverRequiringUpdate(COVER_REQUIRING_UPDATE.NONE);

		handleEventChange({
			name: "isCoverStateValid",
			value: true,
		});
	}, [isUpdateCoverSuccess, outcomeData?.covers?.hasLifeCover]);

	const frequencyDisplay = { monthly: "monthly", annual: "yearly" };

	//*
	const createCoverHeadline = ({
		coverRequested = false,
		crossSellAvailable = false,
		coverType,
		headlineCoverType,
		skeletonColor = "initial",
		coverTypeCheck,
	}) => {
		if (coverRequested) {
			return (
				<Stack direction={"row"}>
					<Typography
						sx={{ width: "auto !important", textAlign: "start" }}
						variant="h5"
					>
						{outcomeData?.firstName}, you have requested
						<Typography variant="h5-bold">
							{" "}
							{headlineCoverType} Cover of{" "}
							{coverTypeCheck === true ? (
								formatCurrencyNoDecimal(
									outcomeData?.covers?.[coverType]?.sumInsured
								)
							) : (
								<Skeleton
									sx={{
										bgcolor: skeletonColor,
										display: "inline-flex",
										marginBottom: "-2px",
									}}
									animation="wave"
									variant="rounded"
									width={140}
									height={20}
								/>
							)}
						</Typography>
					</Typography>
				</Stack>
			);
		}

		if (crossSellAvailable) {
			return (
				<Typography
					sx={{ width: "auto !important", textAlign: "start" }}
					variant="h5"
				>
					Good News! Add{" "}
					{formatCurrencyAbbreviation(
						outcomeData?.covers?.[coverType]?.upSellSums?.premium
					)}{" "}
					Critical Illness cover to you policy for
					<Typography variant="h5-bold">
						{" "}
						{formatCurrency(
							outcomeData?.covers?.[coverType]?.upSellSums?.[frequency]
						)}{" "}
						per {frequency === "monthly" ? "month" : "year"}, or choose another
						amount to suit your needs.
					</Typography>
				</Typography>
			);
		}
	};

	const thankYouTitle = (coverTypesOffered) => {
		return outcomeData?.covers?.anyExclusionsOrLoadings ? (
			<Typography variant="p2">
				Thank you {outcomeData?.firstName} for your application, we're pleased
				to advise we can offer {coverTypesOffered} subject to the Special Terms
				and Conditions outlined below.
			</Typography>
		) : (
			<Typography variant="p2">
				Congratulations {outcomeData?.firstName}, we're pleased to accept your
				application for insurance.
			</Typography>
		);
	};

	const constructDeclineList = ({ coverType, declineList }) => {
		return (
			<>
				<Typography variant="p2">
					{`We're unable to accept your application for ${coverType}
          cover due to the following reasons:`}
				</Typography>
				<List>
					{declineList?.map((child) => {
						return (
							<ListItem
								key={child}
								sx={{
									pl: 0,
									pt: 0.5,
									pb: 0,
									display: "flex",
									alignItems: "baseline",
								}}
								disableGutters
								disableRipple
							>
								<ListItemIcon sx={{ minWidth: "24px" }}>
									<CircleIcon
										sx={{
											fill: "#414141",
											height: "8px",
											width: "8px",
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primaryTypographyProps={{
										variant: "p3-bold",
										sx: { lineHeight: "16px" },
									}}
									primary={child}
									sx={{
										display: "flex",
										alignItems: "flex-start",
										fontSize: "16px",
									}}
								/>
							</ListItem>
						);
					})}
				</List>
			</>
		);
	};

	const declineList = () => {
		let fullDeclinedList = {};
		const PUW =
			outcomeData?.covers?.hasUnableToWorkDecline === true
				? constructDeclineList({
						coverType: "Permanently Unable to Work",
						declineList: outcomeData?.covers?.unableToWork?.declineReasonList,
					})
				: null;

		const CI =
			outcomeData?.covers?.hasCriticalIllnessDecline === true
				? constructDeclineList({
						coverType: "Critical Illness",
						declineList:
							outcomeData?.covers?.criticalIllness?.declineReasonList,
					})
				: null;

		fullDeclinedList = (
			<Stack>
				{CI} {PUW && CI ? <Section /> : null}
				{PUW}
			</Stack>
		);

		return fullDeclinedList;
	};

	const buildCoverList = () => {
		const showLife = outcomeData?.covers?.hasLifeCover;
		const showCI =
			outcomeData?.covers?.hasCriticalIllnessCover &&
			!outcomeData?.covers?.hasCriticalIllnessDecline;
		const showPUW =
			outcomeData?.covers?.hasUnableToWorkCover &&
			!outcomeData?.covers?.hasUnableToWorkDecline;

		const lifeCover = showLife ? "Life cover," : "";
		const ciCover = showCI ? " Critical Illness cover," : "";
		const puwCover = showPUW ? " Permanently unable to work cover," : "";

		return lifeCover + ciCover + puwCover;
	};

	//! Update covers if no error (other than accept terms) ----------------------------------------------------------------
	useEffect(() => {
		const updateCoverAmounts = async () => {
			//* Check that everything on the page is valid before trying to update
			if (dataSet == null || dataSet?.dataUpdating === true) return;

			trigger().then(async (isValid) => {
				if (!isValid && anyErrors() === true) return;
				else {
					switch (coverRequiringUpdate) {
						case COVER_REQUIRING_UPDATE.NONE:
							break;
						case COVER_REQUIRING_UPDATE.LIFE:
						case COVER_REQUIRING_UPDATE.CI:
						case COVER_REQUIRING_UPDATE.PUW:
							if (anyErrors() === false) {
								updateCoverMutate({
									updateData: dataSet,
									accessToken: accessToken,
									outcomeData: outcomeData,
								});
							}
							break;
						default:
							break;
					}
				}
			});
		};
		updateCoverAmounts();
	}, [dataSet, dataSet?.dataUpdating, coverRequiringUpdate]);

	// //* Grab the JWT token and the
	// //* additionalConditions node (key) from the
	// //* preQualificationState
	let additionalLifeConditions = false;
	let additionalCIConditions = false;
	let additionalPUWConditions = false;
	let overallAdditionConditions = false;

	const handleEditCheck = (e, val) => {
		switch (e) {
			case "additionalConditionsLife-Edit-Checkbox":
				additionalLifeConditions = !additionalLifeConditions;
				break;
			case "additionalConditionsCriticalIllness-Edit-Checkbox":
				additionalCIConditions = !additionalCIConditions;
				break;
			case "additionalConditionsUnableToWork-Edit-Checkbox":
				additionalPUWConditions = !additionalPUWConditions;
				break;
			default:
				break;
		}
		overallAdditionConditions =
			additionalLifeConditions ||
			additionalCIConditions ||
			additionalPUWConditions;
		onAdditionConditions(overallAdditionConditions);
		if (val === true) {
			setResetCIAdditional(false);
		}
	};
	//* ----------------------------------------------------------------
	return (
		<>
			<Backdrop
				sx={{ color: "#008ED6", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={open}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Section xs={10} md={7} lg={10}>
				{thankYouTitle(buildCoverList())}
			</Section>
			{outcomeData?.covers?.anyDeclined ? (
				<OneColumnStack maxWidth="690px">
					<NotificationCard
						cardType="info"
						title="Note:"
						message={declineList()}
					/>
				</OneColumnStack>
			) : null}
			{outcomeData?.covers?.hasLifeCover === true ? (
				<Section mt={2}>
					<OutcomeCurrencyInputCard
						name={policyOutcomeFieldNames(person).LIFE_COVER}
						defaultValue={outcomeData?.covers?.life?.sumInsured}
						headline={createCoverHeadline({
							coverRequested: true,
							coverType: "life",
							headlineCoverType: "Life",
							skeletonColor: theme.palette.soft.softBeige150,
							coverTypeCheck: isLifeCoverUpdated,
						})}
						title="Life cover"
						subTitle={`Please select cover amount between ${formatCurrencyNoDecimal(
							LIFE_INSURANCE_MIN[outcomeData?.ageInformation?.minAgeGroup]
						)} and  ${formatCurrencyNoDecimal(
							LIFE_INSURANCE_MAX[outcomeData?.ageInformation?.maxAgeGroup]
						)} `}
						placeholder="Cover amount"
						label="Cover amount"
						backgroundColor={`${theme.palette.soft.softBeige150}`}
						showPremium={lifeCoverValid === true && isLifeCoverUpdated === true}
						frequency={toUpperFirstCharacterFirstWord(
							frequencyDisplay[frequency]
						)}
						premiumAmount={
							lifeCoverValid === true && isLifeCoverUpdated === true
								? formatCurrency(outcomeData?.covers?.life[frequency])
								: null
						}
						{...(!isOnline && { tooltipWidth: 700 })}
						tooltipText={
							isOnline
								? selectCoverTooltips.LIFE_COVER_TOOLTIP
								: selectCoverTooltips.LIFE_COVER_TOOLTIP_CONTACT_CENTRE
						}
						onBlur={(e) => {
							handleOnBlur(e);
						}}
						showSpecialTerms={outcomeData?.covers?.life?.showExclusionCodes}
						specialTerms={outcomeData?.covers?.life?.exclusionCodes}
						hasLoading={outcomeData?.covers?.hasLifeLoading}
						loadings={outcomeData?.covers?.life?.totalLoading}
					/>
					{/* Additional Life Exclusions and Loadings */}
					{!isRefDataLoading &&
					isContactCenter === true &&
					isUWUser === true &&
					outcomeData?.covers?.hasLifeCover === true ? (
						<>
							<AdditionalInformation
								name={
									contactCenterPolicyOutcomeFieldNames()
										.LIFE_ADDITIONAL_CONDITIONS
								}
								coverType="Life"
								dataValues={auraExclusionWordingList[0]?.lifeExclusions[0]}
								showBackdrop={(val) => setOpen(val)}
								backgroundColor={`${theme.palette.soft.softBeige150}`}
								editChecked={(e, val) => handleEditCheck(e, val)}
								resetValues={false}
							/>
						</>
					) : null}
				</Section>
			) : null}
			{/* #region Critical Illness */}
			{outcomeData?.covers?.hasCriticalIllnessCover === true ||
			showCriticalIllness ||
			outcomeData?.covers?.hasCriticalIllnessUpSell === true ? (
				<>
					{outcomeData?.covers?.hasCriticalIllnessUpSell === true &&
					!isOnline ? (
						<OneColumnStack maxWidth="690px">
							<NotificationCard
								cardType="info"
								title=""
								alignIcon="flex-start"
								message={
									policyOutcomeDisclaimers({
										isOnline: false,
									}).policyOutcomeInfo[0].OUTCOME_CROSS_SELL.note
								}
							/>
						</OneColumnStack>
					) : null}
					<Section mt={2} data-identifier="critical-illness-outcome">
						<OutcomeCurrencyInputCard
							name={policyOutcomeFieldNames(person).CRITICAL_ILLNESS_COVER}
							defaultValue={outcomeData?.covers?.criticalIllness?.sumInsured}
							headline={createCoverHeadline({
								coverRequested: showCIHeadline,
								crossSellAvailable:
									outcomeData?.covers?.hasCriticalIllnessUpSell &&
									upSellSelected === false,
								skeletonColor: theme.palette.greyscale.Grey600,
								coverType: "criticalIllness",
								headlineCoverType: "Critical Illness",
								coverTypeCheck: isCriticalIllnessCoverUpdated,
							})}
							title="Critical Illness cover (optional)"
							subTitle={
								upSellSelected === true ||
								outcomeData?.covers?.hasCriticalIllnessCover
									? `Please select cover amount between ${formatCurrencyNoDecimal(
											CRITICAL_ILLNESS_MIN[
												outcomeData?.ageInformation?.minAgeGroup
											]
										)} and ${formatCurrencyNoDecimal(
											CRITICAL_ILLNESS_MAX[
												outcomeData?.ageInformation?.maxAgeGroup
											]
										)}`
									: policyOutcomeDisclaimers({
											isOnline: true,
										}).policyOutcomeInfo[0].UPSELL_NOTIFICATION.note
							}
							subTitleJsx={!upSellSelected}
							placeholder="Cover amount"
							label="Cover amount"
							backgroundColor={`${theme.palette.greyscale.Grey600}`}
							showPremium={isCriticalIllnessCoverUpdated}
							showCheckbox={true}
							frequency={toUpperFirstCharacterFirstWord(
								frequencyDisplay[frequency]
							)}
							checked={isCIChecked}
							premiumAmount={
								isCriticalIllnessCoverUpdated &&
								outcomeData?.covers?.hasCriticalIllnessCover
									? formatCurrency(
											outcomeData?.covers?.criticalIllness[frequency]
										)
									: null
							}
							checkedOnChange={(e) => {
								handleCriticalIllnessCheckedChange(e);
							}}
							onBlur={(e) => {
								handleOnBlur(e);
							}}
							{...(!isOnline && { tooltipWidth: 700 })}
							tooltipText={
								isOnline
									? selectCoverTooltips.CRITICAL_ILLNESS_TOOLTIP
									: selectCoverTooltips.CRITICAL_ILLNESS_TOOLTIP_CONTACT_CENTRE
							}
							showSpecialTerms={showCIExclusions}
							specialTerms={
								outcomeData?.covers?.criticalIllness?.exclusionCodes
							}
							hasLoading={showCILoading}
							loadings={outcomeData?.covers?.criticalIllness?.totalLoading}
						/>

						{showAllTraumaCover === true && pageLoaded === true ? (
							<Section
								textAlign="left"
								sx={{ px: 3, pb: 3, maxWidth: { xs: "690px" } }}
								mb={0}
								mt={4}
							>
								<RHFCircleToggleGroupLabelled
									variant="circle-group"
									name={policyOutcomeFieldNames(person).TRAUMA_COVER}
									items={questionTypes.YES_NO}
									label={{ text: questionLabels.TRAUMA_COVER_QUESTION.label }}
									description={
										isOnline
											? questionLabels.TRAUMA_COVER_QUESTION.description.online
											: questionLabels.TRAUMA_COVER_QUESTION.description
													.contactCentre
									}
									position={{ xs: "center", sm: "left" }}
									minHeight={0}
								/>
							</Section>
						) : null}
						{/* Additional CI Exclusions and Loadings */}
						{!isRefDataLoading &&
						isContactCenter === true &&
						isUWUser === true &&
						outcomeData?.covers?.hasCriticalIllnessCover === true ? (
							<>
								<AdditionalInformation
									name={
										contactCenterPolicyOutcomeFieldNames()
											.CRITICAL_ILLNESS_ADDITIONAL_CONDITIONS
									}
									coverType="CriticalIllness"
									dataValues={auraExclusionWordingList[1]?.ciExclusions[0]}
									showBackdrop={(val) => setOpen(val)}
									backgroundColor={`${theme.palette.greyscale.Grey600}`}
									editChecked={(e, val) => handleEditCheck(e, val)}
									resetValues={resetCIAdditional}
								/>
							</>
						) : null}
					</Section>
				</>
			) : null}
			{/* #endregion */}
			{outcomeData?.covers?.hasUnableToWorkCover === true ||
			showUnableToWork ? (
				<Section mt={2}>
					<OutcomeCurrencyInputCard
						name={policyOutcomeFieldNames(person).PERMANENT_DISABILITY_COVER}
						defaultValue={outcomeData?.covers?.unableToWork?.sumInsured}
						headline={createCoverHeadline({
							coverRequested: showPUWHeadline,
							coverType: "unableToWork",
							headlineCoverType: "Permanently unable to work",
							skeletonColor: theme.palette.greyscale.Grey600,
							coverTypeCheck: isPermanentDisabilityUpdated,
						})}
						title="Permanently unable to work cover (optional)"
						subTitle={`Please select cover amount between ${formatCurrencyNoDecimal(
							PERMANENT_DISABILITY_MIN[outcomeData?.ageInformation?.minAgeGroup]
						)} and ${formatCurrencyNoDecimal(
							PERMANENT_DISABILITY_MAX[outcomeData?.ageInformation?.maxAgeGroup]
						)}`}
						placeholder="Cover amount"
						label="Cover amount"
						backgroundColor={`${theme.palette.greyscale.Grey600}`}
						showPremium={showPUWPremiums}
						showCheckbox={true}
						frequency={toUpperFirstCharacterFirstWord(
							frequencyDisplay[frequency]
						)}
						checked={isPUWChecked}
						premiumAmount={
							showPUWPremiums && outcomeData?.covers?.hasUnableToWorkCover
								? formatCurrency(outcomeData?.covers?.unableToWork[frequency])
								: null
						}
						checkedOnChange={(e) => {
							handleUnableToWorkCheckedChange(e);
						}}
						onBlur={(e) => {
							handleOnBlur(e);
						}}
						{...(!isOnline && { tooltipWidth: 700 })}
						tooltipText={
							isOnline
								? selectCoverTooltips.PERMANENT_DISABILITY_TOOLTIP
								: selectCoverTooltips.PERMANENT_DISABILITY_TOOLTIP_CONTACT_CENTRE
						}
						showSpecialTerms={showPUWExclusions}
						specialTerms={outcomeData?.covers?.unableToWork?.exclusionCodes}
						hasLoading={showPUWLoading}
						loadings={outcomeData?.covers?.unableToWork?.totalLoading}
					/>

					{/* Additional PUW Exclusions and Loadings */}
					{!isRefDataLoading &&
					isContactCenter === true &&
					isUWUser === true &&
					outcomeData?.covers?.hasUnableToWorkCover === true ? (
						<>
							<AdditionalInformation
								name={
									contactCenterPolicyOutcomeFieldNames()
										.PERMANENT_DISABILITY_ADDITIONAL_CONDITIONS
								}
								coverType="UnableToWork"
								dataValues={auraExclusionWordingList[2]?.puwExclusions[0]}
								showBackdrop={(val) => setOpen(val)}
								backgroundColor={`${theme.palette.greyscale.Grey600}`}
								editChecked={(e, val) => handleEditCheck(e, val)}
								resetValues={false}
							/>
						</>
					) : null}
				</Section>
			) : null}
		</>
	);
}
